/**
 * Modern Document Upload Modal Component
 * Supports single upload button for both single and dual-sided documents
 * Files are converted to base64 and stored in database for admin review
 * Fully theme-aware with sleek design
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
    CloudArrowUpIcon,
    DocumentIcon,
    XMarkIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    ArrowPathIcon,
    PhotoIcon,
    IdentificationIcon,
    CreditCardIcon,
    DocumentTextIcon,
    UserCircleIcon,
    SparklesIcon
} from '@heroicons/react/24/outline';
import {
    CheckCircleIcon as CheckCircleSolid,
    DocumentIcon as DocumentSolid
} from '@heroicons/react/24/solid';
import Modal from '../ui/Modal';
import { DocumentType, DocumentUploadData } from '../../types/verification';
import { DOCUMENT_CONFIGS, UPLOAD_CONSTRAINTS } from '../../constants/verification';
import { verificationService } from '../../services/verificationService';
import { useAppDispatch } from '../../store/hooks';
import { showSnackbar } from '../../store/slices/uiSlice';
import { useVerificationSocket } from '../../hooks/useVerificationSocket';

interface DocumentUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    onUploadComplete?: () => void;
    allowedDocumentTypes?: DocumentType[];
    title?: string;
    hasRejectedDocuments?: boolean;
    rejectedDocumentsCount?: number;
}

interface UploadState {
    isUploading: boolean;
    uploadProgress: number;
    isComplete: boolean;
    error?: string;
    documentId?: string;
}

const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({
    isOpen,
    onClose,
    onUploadComplete,
    allowedDocumentTypes,
    title = 'Upload Verification Documents',
    hasRejectedDocuments = false,
    rejectedDocumentsCount = 0
}) => {
    const dispatch = useAppDispatch();
    const { getUploadProgress } = useVerificationSocket();
    const [selectedDocumentType, setSelectedDocumentType] = useState<DocumentType | null>(null);
    const [frontFile, setFrontFile] = useState<File | null>(null);
    const [backFile, setBackFile] = useState<File | null>(null);
    const [singleFile, setSingleFile] = useState<File | null>(null);
    const [dragActive, setDragActive] = useState(false);
    const [description, setDescription] = useState('');
    const [uploadState, setUploadState] = useState<UploadState>({
        isUploading: false,
        uploadProgress: 0,
        isComplete: false
    });

    const frontFileInputRef = useRef<HTMLInputElement>(null);
    const backFileInputRef = useRef<HTMLInputElement>(null);
    const singleFileInputRef = useRef<HTMLInputElement>(null);

    // Check if document type requires dual-sided upload
    const isDualSided = selectedDocumentType && DOCUMENT_CONFIGS[selectedDocumentType]?.requiresFrontBack;

    // Get document type icon
    const getDocumentTypeIcon = (docType: DocumentType) => {
        switch (docType) {
            case 'national_id':
                return IdentificationIcon;
            case 'drivers_license':
                return CreditCardIcon;
            case 'voters_card':
                return UserCircleIcon;
            case 'passport':
                return DocumentTextIcon;
            default:
                return DocumentIcon;
        }
    };

    // Get document type color theme
    const getDocumentTypeTheme = (docType: DocumentType) => {
        switch (docType) {
            case 'national_id':
                return 'from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 border-primary-200 dark:border-primary-700/50 text-primary-700 dark:text-primary-300';
            case 'drivers_license':
                return 'from-secondary-50 to-secondary-100 dark:from-secondary-900/20 dark:to-secondary-800/20 border-secondary-200 dark:border-secondary-700/50 text-secondary-700 dark:text-secondary-300';
            case 'voters_card':
                return 'from-accent-50 to-accent-100 dark:from-accent-900/20 dark:to-accent-800/20 border-accent-200 dark:border-accent-700/50 text-accent-700 dark:text-accent-300';
            case 'passport':
                return 'from-coral-50 to-coral-100 dark:from-coral-900/20 dark:to-coral-800/20 border-coral-200 dark:border-coral-700/50 text-coral-700 dark:text-coral-300';
            default:
                return 'from-neutral-50 to-neutral-100 dark:from-neutral-800/50 dark:to-neutral-700/50 border-neutral-200 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300';
        }
    };

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            resetState();
        }
    }, [isOpen]);

    const resetState = useCallback(() => {
        setSelectedDocumentType(null);
        setFrontFile(null);
        setBackFile(null);
        setSingleFile(null);
        setDescription('');
        setUploadState({
            isUploading: false,
            uploadProgress: 0,
            isComplete: false
        });
    }, []);

    // File validation
    const validateFile = (file: File): { isValid: boolean; error?: string } => {
        const maxSize = UPLOAD_CONSTRAINTS.maxFileSize;
        const allowedTypes = UPLOAD_CONSTRAINTS.supportedFormats;

        if (file.size > maxSize) {
            return {
                isValid: false,
                error: `File size exceeds ${maxSize / (1024 * 1024)}MB limit`
            };
        }

        if (!allowedTypes.includes(file.type)) {
            return {
                isValid: false,
                error: 'Invalid file type. Only JPEG, PNG, WebP, and PDF are allowed.'
            };
        }

        return { isValid: true };
    };

    // Handle file selection
    const handleFileSelect = useCallback((files: FileList | null, type: 'front' | 'back' | 'single') => {
        if (!files || files.length === 0) return;

        const file = files[0];
        const validation = validateFile(file);

        if (!validation.isValid) {
            setUploadState(prev => ({ ...prev, error: validation.error }));
            return;
        }

        // Clear any previous errors
        setUploadState(prev => ({ ...prev, error: undefined }));

        switch (type) {
            case 'front':
                setFrontFile(file);
                break;
            case 'back':
                setBackFile(file);
                break;
            case 'single':
                setSingleFile(file);
                break;
        }
    }, []);

    // Handle drag and drop
    const handleDrag = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    }, []);

    const handleDrop = useCallback((e: React.DragEvent, type: 'front' | 'back' | 'single') => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        const files = e.dataTransfer.files;
        handleFileSelect(files, type);
    }, [handleFileSelect]);

    // Check if upload is ready
    const isUploadReady = selectedDocumentType && (
        isDualSided ? (frontFile && backFile) : singleFile
    );

    // Handle upload
    const handleUpload = useCallback(async () => {
        if (!isUploadReady || !selectedDocumentType) return;

        setUploadState({
            isUploading: true,
            uploadProgress: 0,
            isComplete: false,
            error: undefined
        });

        try {
            const uploadData: DocumentUploadData = {
                documentType: selectedDocumentType,
                description: description.trim() || undefined
            };

            if (isDualSided && frontFile && backFile) {
                uploadData.frontFile = frontFile;
                uploadData.backFile = backFile;
            } else if (singleFile) {
                uploadData.file = singleFile;
            }

            const result = await verificationService.uploadDocument(
                uploadData,
                (progress) => {
                    setUploadState(prev => ({
                        ...prev,
                        uploadProgress: progress
                    }));
                }
            );

            if (result.success) {
                const documentId = result.data.documentId || result.data.frontDocumentId;

                if (!documentId) {
                    throw new Error('Upload completed but no document ID was returned.');
                }

                setUploadState({
                    isUploading: false,
                    uploadProgress: 100,
                    isComplete: true,
                    documentId: documentId
                });

                // Check for real-time socket progress updates if available
                const socketProgress = getUploadProgress(documentId);
                if (typeof socketProgress === 'number' && socketProgress > 0 && socketProgress < 100) {
                    setUploadState(prev => ({
                        ...prev,
                        uploadProgress: socketProgress
                    }));
                }

                // Show success message
                dispatch(showSnackbar({
                    message: 'Document uploaded successfully! It will be reviewed by our team.',
                    type: 'success',
                    duration: 4000
                }));

                // Call completion callback after a short delay
                setTimeout(() => {
                    onUploadComplete?.();
                    onClose();
                }, 2000);
            } else {
                throw new Error(result.message || 'Upload failed');
            }

        } catch (error: any) {
            // Extract the backend error message if available
            const errorMessage = error.response?.data?.message ||
                error.message ||
                'Upload failed. Please try again.';

            // Show snackbar alert for the error
            dispatch(showSnackbar({
                message: errorMessage,
                type: 'error',
                duration: 6000
            }));

            setUploadState({
                isUploading: false,
                uploadProgress: 0,
                isComplete: false,
                error: errorMessage
            });
        }
    }, [isUploadReady, selectedDocumentType, isDualSided, frontFile, backFile, singleFile, description, onUploadComplete, onClose, dispatch, getUploadProgress]);

    // Get available document types
    const availableTypes = allowedDocumentTypes || Object.keys(DOCUMENT_CONFIGS) as DocumentType[];

    // Render file upload area with modern design
    const renderFileUploadArea = (type: 'front' | 'back' | 'single', label: string, file: File | null) => {
        const inputRef = type === 'front' ? frontFileInputRef :
            type === 'back' ? backFileInputRef :
                singleFileInputRef;

        const getUploadStateClasses = () => {
            if (file) {
                return 'border-success-300 dark:border-success-600 bg-gradient-to-br from-success-50 to-success-100 dark:from-success-900/20 dark:to-success-800/30 shadow-soft';
            }
            if (dragActive) {
                return 'border-primary-400 dark:border-primary-500 bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/30 dark:to-primary-800/40 shadow-medium scale-[1.02]';
            }
            return 'border-neutral-200 dark:border-neutral-700 bg-gradient-to-br from-neutral-50 to-white dark:from-neutral-800/50 dark:to-neutral-900/50 hover:border-primary-300 dark:hover:border-primary-600 hover:from-primary-25 hover:to-primary-50 dark:hover:from-primary-950/50 dark:hover:to-primary-900/50 hover:shadow-soft';
        };

        return (
            <div className="space-y-3">
                <label className="flex items-center gap-2 text-sm font-semibold text-neutral-800 dark:text-neutral-200">
                    <PhotoIcon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                    {label}
                    {isDualSided && type === 'front' && (
                        <span className="text-xs px-2 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full font-medium">
                            Required
                        </span>
                    )}
                </label>
                <div
                    className={`
                        relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer
                        transition-all duration-300 ease-out transform
                        ${getUploadStateClasses()}
                    `}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={(e) => handleDrop(e, type)}
                    onClick={() => inputRef.current?.click()}
                >
                    <input
                        ref={inputRef}
                        type="file"
                        className="hidden"
                        accept={UPLOAD_CONSTRAINTS.supportedFormats.join(',')}
                        onChange={(e) => handleFileSelect(e.target.files, type)}
                    />

                    {file ? (
                        <div className="space-y-4">
                            <div className="relative">
                                <CheckCircleSolid className="w-16 h-16 text-success-500 dark:text-success-400 mx-auto" />
                                <div className="absolute -top-1 -right-1">
                                    <SparklesIcon className="w-6 h-6 text-success-400 dark:text-success-300 animate-pulse" />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <p className="text-sm font-semibold text-success-700 dark:text-success-300 truncate max-w-full">
                                    {file.name}
                                </p>
                                <div className="flex items-center justify-center gap-2">
                                    <span className="text-xs px-3 py-1 bg-success-100 dark:bg-success-900/30 text-success-600 dark:text-success-400 rounded-full font-medium">
                                        {(file.size / (1024 * 1024)).toFixed(2)} MB
                                    </span>
                                    <span className="text-xs text-success-500 dark:text-success-400">✓ Ready</span>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/40 border border-primary-200 dark:border-primary-700 rounded-lg transition-colors duration-200"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    switch (type) {
                                        case 'front':
                                            setFrontFile(null);
                                            break;
                                        case 'back':
                                            setBackFile(null);
                                            break;
                                        case 'single':
                                            setSingleFile(null);
                                            break;
                                    }
                                }}
                            >
                                <ArrowPathIcon className="w-4 h-4" />
                                Change file
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="relative">
                                <CloudArrowUpIcon className="w-16 h-16 text-neutral-400 dark:text-neutral-500 mx-auto" />
                                {dragActive && (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="w-20 h-20 border-4 border-primary-200 dark:border-primary-700 border-dashed rounded-full animate-ping" />
                                    </div>
                                )}
                            </div>
                            <div className="space-y-3">
                                <div>
                                    <p className="text-base font-medium text-neutral-700 dark:text-neutral-200">
                                        <span className="font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors">
                                            Click to upload
                                        </span> or drag and drop
                                    </p>
                                    <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
                                        PNG, JPG, WebP or PDF up to {UPLOAD_CONSTRAINTS.maxFileSize / (1024 * 1024)}MB
                                    </p>
                                </div>
                                <div className="flex items-center justify-center gap-2 pt-2">
                                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-neutral-200 dark:via-neutral-700 to-transparent" />
                                    <span className="text-xs text-neutral-400 dark:text-neutral-500 font-medium px-2">
                                        Secure Upload
                                    </span>
                                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-neutral-200 dark:via-neutral-700 to-transparent" />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={title} size="lg">
            <div className="p-6 space-y-8 min-h-[500px]">
                {/* Rejected Documents Warning - Show if user has rejected documents */}
                {hasRejectedDocuments && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
                        <div className="flex items-start gap-4">
                            <ExclamationTriangleIcon className="w-8 h-8 text-red-600 dark:text-red-400 flex-shrink-0" />
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
                                    Upload Not Allowed
                                </h3>
                                <p className="text-red-700 dark:text-red-300 mb-4 leading-relaxed">
                                    You have <strong>{rejectedDocumentsCount} rejected document{rejectedDocumentsCount > 1 ? 's' : ''}</strong> that must be deleted before uploading new documents. This policy prevents duplicate submissions and ensures a clean verification process.
                                </p>
                                <div className="bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg p-4 mb-4">
                                    <h4 className="font-semibold text-red-800 dark:text-red-200 mb-2">
                                        Next Steps:
                                    </h4>
                                    <ol className="list-decimal list-inside space-y-1 text-sm text-red-700 dark:text-red-300">
                                        <li>Close this modal</li>
                                        <li>Go to the "Documents" tab</li>
                                        <li>Delete all rejected documents</li>
                                        <li>Return here to upload new documents</li>
                                    </ol>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                                >
                                    Close and Manage Documents
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Only show upload interface if no rejected documents */}
                {!hasRejectedDocuments && uploadState.isComplete ? (
                    // Success state with celebration design
                    <div className="text-center py-12 space-y-6">
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-32 h-32 bg-gradient-to-r from-success-100 to-success-200 dark:from-success-900/30 dark:to-success-800/40 rounded-full animate-pulse" />
                            </div>
                            <CheckCircleSolid className="relative w-20 h-20 text-success-500 dark:text-success-400 mx-auto" />
                            <div className="absolute -top-2 -right-2">
                                <SparklesIcon className="w-8 h-8 text-success-400 dark:text-success-300 animate-bounce" />
                            </div>
                        </div>
                        <div className="space-y-4">
                            <h3 className="text-2xl font-bold bg-gradient-to-r from-success-600 to-success-700 dark:from-success-400 dark:to-success-300 bg-clip-text text-transparent">
                                Upload Successful! 🎉
                            </h3>
                            <p className="text-lg text-success-700 dark:text-success-300 font-medium max-w-md mx-auto">
                                Your document has been securely uploaded and sent to our verification team.
                            </p>
                            <div className="bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-700/50 rounded-xl p-4 max-w-lg mx-auto">
                                <p className="text-sm text-success-700 dark:text-success-300 font-medium">
                                    📧 You'll receive email updates on your verification status
                                </p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Step 1: Modern Document Type Selection */}
                        {!selectedDocumentType && (
                            <div className="space-y-6">
                                <div className="text-center space-y-2">
                                    <h3 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                                        Choose Your Document
                                    </h3>
                                    <p className="text-neutral-600 dark:text-neutral-400">
                                        Select the type of identification document you'd like to upload
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 gap-4">
                                    {availableTypes.map((docType) => {
                                        const config = DOCUMENT_CONFIGS[docType];
                                        const IconComponent = getDocumentTypeIcon(docType);
                                        const themeClasses = getDocumentTypeTheme(docType);
                                        const isDualSidedDoc = ['national_id', 'driver_license', 'voter_card'].includes(docType);

                                        return (
                                            <button
                                                key={docType}
                                                className={`
                                                    group relative flex items-center p-6 border-2 rounded-2xl
                                                    transition-all duration-300 ease-out transform text-left
                                                    hover:scale-[1.02] hover:shadow-medium active:scale-[0.98]
                                                    bg-gradient-to-br ${themeClasses}
                                                `}
                                                onClick={() => setSelectedDocumentType(docType)}
                                            >
                                                <div className="relative">
                                                    <div className="p-3 rounded-xl bg-white/80 dark:bg-neutral-700/50 shadow-soft group-hover:shadow-medium transition-shadow duration-300">
                                                        <IconComponent className="w-8 h-8" />
                                                    </div>
                                                    {isDualSidedDoc && (
                                                        <div className="absolute -top-1 -right-1">
                                                            <div className="w-5 h-5 bg-accent-500 dark:bg-accent-400 rounded-full flex items-center justify-center">
                                                                <span className="text-xs font-bold text-white dark:text-neutral-900">2</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="ml-4 flex-1 min-w-0">
                                                    <div className="flex items-center gap-3 mb-1">
                                                        <h4 className="font-semibold text-lg truncate">
                                                            {config.displayName}
                                                        </h4>
                                                        {isDualSidedDoc && (
                                                            <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-accent-100 dark:bg-accent-900/30 text-accent-700 dark:text-accent-300 rounded-full border border-accent-200 dark:border-accent-700/50">
                                                                Both Sides
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-sm opacity-90 line-clamp-2">
                                                        {config.description}
                                                    </p>
                                                </div>

                                                <div className="ml-3 opacity-60 group-hover:opacity-100 transition-opacity duration-300">
                                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                    </svg>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>

                                <div className="text-center pt-4">
                                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-info-50 dark:bg-info-900/20 text-info-700 dark:text-info-300 rounded-lg border border-info-200 dark:border-info-700/50">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                        <span className="text-sm font-medium">All uploads are encrypted and secure</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Step 2: Modern File Upload Interface */}
                        {selectedDocumentType && !uploadState.isUploading && (
                            <div className="space-y-8">
                                {/* Header with back button */}
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-xl bg-primary-100 dark:bg-primary-900/30">
                                            {React.createElement(getDocumentTypeIcon(selectedDocumentType), {
                                                className: "w-6 h-6 text-primary-600 dark:text-primary-400"
                                            })}
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
                                                Upload {DOCUMENT_CONFIGS[selectedDocumentType].displayName}
                                            </h3>
                                            <p className="text-sm text-neutral-600 dark:text-neutral-400">
                                                {DOCUMENT_CONFIGS[selectedDocumentType].description}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200 bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded-lg transition-colors duration-200"
                                        onClick={() => setSelectedDocumentType(null)}
                                    >
                                        <ArrowPathIcon className="w-4 h-4" />
                                        Change
                                    </button>
                                </div>

                                {isDualSided ? (
                                    <div className="space-y-6">
                                        {/* Dual-sided info banner */}
                                        <div className="relative overflow-hidden bg-gradient-to-r from-info-50 to-info-100 dark:from-info-900/20 dark:to-info-800/30 border border-info-200 dark:border-info-700/50 rounded-2xl p-6">
                                            <div className="absolute top-0 right-0 w-32 h-32 transform translate-x-16 -translate-y-16">
                                                <div className="w-full h-full bg-info-200/20 dark:bg-info-700/20 rounded-full" />
                                            </div>
                                            <div className="relative flex items-start gap-4">
                                                <div className="flex-shrink-0">
                                                    <div className="w-12 h-12 bg-info-500 dark:bg-info-400 rounded-xl flex items-center justify-center">
                                                        <span className="text-xl font-bold text-white dark:text-neutral-900">2</span>
                                                    </div>
                                                </div>
                                                <div className="flex-1">
                                                    <h4 className="font-semibold text-info-800 dark:text-info-200 mb-2">
                                                        Both Sides Required
                                                    </h4>
                                                    <p className="text-sm text-info-700 dark:text-info-300 leading-relaxed">
                                                        This document requires both front and back images for complete verification.
                                                        Please ensure both sides are clear and readable.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* File upload areas */}
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                            {renderFileUploadArea('front', 'Front Side', frontFile)}
                                            {renderFileUploadArea('back', 'Back Side', backFile)}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-6">
                                        {renderFileUploadArea('single', 'Document File', singleFile)}
                                    </div>
                                )}

                                {/* Modern Description Input */}
                                <div className="space-y-3">
                                    <label className="flex items-center gap-2 text-sm font-semibold text-neutral-800 dark:text-neutral-200">
                                        <DocumentTextIcon className="w-4 h-4 text-neutral-600 dark:text-neutral-400" />
                                        Additional Notes
                                        <span className="text-xs px-2 py-0.5 bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 rounded-full font-normal">
                                            Optional
                                        </span>
                                    </label>
                                    <div className="relative">
                                        <textarea
                                            className="w-full px-4 py-3 bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:focus:ring-primary-400 dark:focus:border-primary-400 transition-colors duration-200 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 dark:placeholder:text-neutral-400 resize-none"
                                            rows={3}
                                            placeholder="Any additional information about this document (e.g., special circumstances, document condition, etc.)"
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                            maxLength={500}
                                        />
                                        <div className="absolute bottom-3 right-3 text-xs text-neutral-400 dark:text-neutral-500 font-mono">
                                            {description.length}/500
                                        </div>
                                    </div>
                                </div>

                                {/* Modern Error Display */}
                                {uploadState.error && (
                                    <div className="flex items-start gap-3 p-4 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-700/50 rounded-xl">
                                        <div className="flex-shrink-0">
                                            <ExclamationTriangleIcon className="w-5 h-5 text-error-500 dark:text-error-400" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-medium text-error-700 dark:text-error-300">
                                                {uploadState.error}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Modern Action Buttons */}
                                <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end pt-4 border-t border-neutral-200 dark:border-neutral-700">
                                    <button
                                        type="button"
                                        className="px-6 py-3 text-sm font-semibold text-neutral-700 dark:text-neutral-300 bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 rounded-xl transition-colors duration-200"
                                        onClick={onClose}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className={`
                                            inline-flex items-center justify-center gap-2 px-8 py-3 text-sm font-semibold
                                            rounded-xl transition-all duration-200 transform
                                            ${isUploadReady
                                                ? 'bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white shadow-soft hover:shadow-medium hover:scale-[1.02] active:scale-[0.98]'
                                                : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400 cursor-not-allowed'
                                            }
                                        `}
                                        onClick={handleUpload}
                                        disabled={!isUploadReady}
                                    >
                                        {isUploadReady && (
                                            <CheckCircleIcon className="w-4 h-4" />
                                        )}
                                        Upload {isDualSided ? 'Documents' : 'Document'}
                                        {isUploadReady && (
                                            <SparklesIcon className="w-4 h-4 animate-pulse" />
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Modern Upload Progress */}
                        {uploadState.isUploading && (
                            <div className="space-y-8 py-12">
                                <div className="text-center space-y-6">
                                    {/* Animated upload icon */}
                                    <div className="relative">
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <div className="w-24 h-24 bg-gradient-to-r from-primary-100 to-primary-200 dark:from-primary-900/30 dark:to-primary-800/40 rounded-full animate-pulse" />
                                        </div>
                                        <CloudArrowUpIcon className="relative w-16 h-16 text-primary-600 dark:text-primary-400 mx-auto animate-bounce" />
                                        <div className="absolute -bottom-1 -right-1">
                                            <ArrowPathIcon className="w-8 h-8 text-primary-500 dark:text-primary-400 animate-spin" />
                                        </div>
                                    </div>

                                    {/* Upload status */}
                                    <div className="space-y-3">
                                        <h3 className="text-2xl font-bold text-primary-700 dark:text-primary-300">
                                            Uploading Your {isDualSided ? 'Documents' : 'Document'}...
                                        </h3>
                                        <p className="text-lg text-neutral-600 dark:text-neutral-400 max-w-md mx-auto">
                                            Please wait while we securely process and encrypt your files
                                        </p>
                                    </div>
                                </div>

                                {/* Modern Progress Bar */}
                                <div className="space-y-4">
                                    <div className="relative w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-3 overflow-hidden">
                                        <div className="absolute inset-0 bg-gradient-to-r from-neutral-300/50 to-neutral-100/50 dark:from-neutral-600/50 dark:to-neutral-800/50 animate-pulse" />
                                        <div
                                            className="relative h-full bg-gradient-to-r from-primary-500 to-primary-600 dark:from-primary-400 dark:to-primary-500 rounded-full transition-all duration-500 ease-out shadow-soft"
                                            style={{ width: `${uploadState.uploadProgress}%` }}
                                        >
                                            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-full" />
                                        </div>
                                    </div>

                                    {/* Progress details */}
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-neutral-600 dark:text-neutral-400 font-medium">
                                            Processing...
                                        </span>
                                        <span className="font-mono font-bold text-primary-600 dark:text-primary-400">
                                            {uploadState.uploadProgress}%
                                        </span>
                                    </div>
                                </div>

                                {/* Security notice */}
                                <div className="text-center">
                                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-success-50 dark:bg-success-900/20 text-success-700 dark:text-success-300 rounded-lg border border-success-200 dark:border-success-700/50">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                        </svg>
                                        <span className="text-sm font-medium">Files are being encrypted for your security</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* Show generic message if rejected documents exist but no success state */}
                {hasRejectedDocuments && !uploadState.isComplete && (
                    <div className="text-center py-8">
                        <p className="text-neutral-500 dark:text-neutral-400 text-sm">
                            Upload is disabled due to rejected documents above.
                        </p>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default DocumentUploadModal;